import * as React from 'react';
import { AppSettings, ISitePage, SaveState, SaveFlowStatus } from '@src/types';
import { IServices, initializeServices } from '../sevices/services';
import { Actions, initializeActions } from './actions/actions';
import { getInitialState } from './helpers/get-initial-state';
import { appDefIds, TModuleParams } from '@wix/business-manager-api';
import { AppInitialData } from '@src/sevices/get-app-data/get-app-data';
import { KBLinks, initKBLinks, DO_NOT_SELL_DATA_APPDEFID } from '@src/constants';
import { I18nextProvider, TranslationFunction } from 'react-i18next';
import { i18n as I18n } from 'i18next';
import i18n, { TranslationNamespace } from '../i18n';

export interface RootProps extends Pick<TModuleParams, 'liveSite' | 'accountLanguage' | 'viewMode'> {
  msid: string;
  translationsStaticsUrl: string;
  services?: IServices | null;
}

export interface SettingsState {
  appSettings: AppSettings;
}

export type Validation = 'not-validated' | 'valid' | 'invalid';

export interface Validations {
  privacyPolicyLink: Validation;
  reviewRequirements: Validation;
}

export interface StoreState extends Pick<RootProps, 'liveSite' | 'viewMode'> {
  isDoNotSellAppInstalled: boolean;
  dataInitialized: boolean;
  siteLanguage: string;
  currentState?: SettingsState;
  pendingState?: SettingsState;
  isAppInstalled: boolean;
  sitePages: ISitePage[];
  saveState: SaveState;
  validations: Validations;
  kbs: KBLinks;
}

export type Store = StoreState & Actions;

export const AppContext = React.createContext({} as Store);

export class StoreProvider extends React.PureComponent<RootProps, StoreState> {
  private readonly services: IServices;
  private readonly actions: Actions;
  private i18n: I18n | null = null;

  constructor(props: RootProps) {
    super(props);
    this.services = props.services || initializeServices();
    this.actions = initializeActions({
      getState: this._getState,
      setState: this._setState,
      services: this.services,
    });

    this.state = {
      viewMode: props.viewMode,
      dataInitialized: false,
      siteLanguage: 'en',
      isDoNotSellAppInstalled: !!this.services.getCurrentInstance(DO_NOT_SELL_DATA_APPDEFID),
      isAppInstalled: this.services.isAppInstalled(appDefIds.consentPolicyBanner),
      liveSite: this.props.liveSite,
      sitePages: [],
      saveState: {
        pendingChanges: false,
        status: SaveFlowStatus.None,
      },
      validations: {
        privacyPolicyLink: 'not-validated',
        reviewRequirements: 'not-validated',
      },
      kbs: initKBLinks((key) => key),
    };
  }

  async componentDidMount() {
    let data: AppInitialData | undefined;
    let currentState: SettingsState | undefined;
    let pendingState: SettingsState | undefined;
    let sitePages: ISitePage[] = [];
    let siteLanguage = 'en';

    try {
      data = await this.services.getAppData(this.props.msid);
      sitePages = data.sitePages;
      siteLanguage = data.siteLanguage;
    } catch (e) {
      console.error(e);
    }

    const { accountLanguage, translationsStaticsUrl } = this.props;
    this.i18n = i18n(accountLanguage, siteLanguage, translationsStaticsUrl);
    let t: TranslationFunction | undefined;

    try {
      await new Promise((resolve, reject) => {
        this.i18n!.on('loaded', resolve);
        this.i18n!.on('failedLoading', (lng, ns, msg) => {
          ns === TranslationNamespace.site ? resolve() : reject();
        });
      });

      t = this.i18n.t.bind(this.i18n);
      if (data) {
        currentState = getInitialState({
          ...this.props,
          ...data,
          t,
        });
        pendingState = { ...currentState };
      }
    } catch (e) {}

    this.setState({
      dataInitialized: true,
      currentState,
      pendingState,
      sitePages,
      siteLanguage,
      kbs: initKBLinks(t || ((key) => key)),
    });
  }

  private _getState = () => this.state;
  private _setState = (newState: Partial<StoreState>) =>
    new Promise((resolve) => this.setState(newState as StoreState, resolve));

  render() {
    const { dataInitialized, currentState } = this.state;

    if (!dataInitialized) {
      return null;
    }

    if (!currentState) {
      throw new Error('Failed to retrieve component settings');
    }

    return (
      <I18nextProvider i18n={this.i18n!}>
        <AppContext.Provider
          value={
            {
              ...this.actions,
              ...this.state,
            } as Store
          }
        >
          {this.props.children}
        </AppContext.Provider>
      </I18nextProvider>
    );
  }
}
